// src/components/DashboardSection.js (Corrected Import)

import React, { useState, useEffect } from 'react';
import { useFirebase } from '../App';
import { collection, query, where, getDocs, Timestamp, limit } from 'firebase/firestore';
import { Bar, Pie } from 'react-chartjs-2'; // CORRECTED IMPORT
import { toast } from 'react-toastify';
import * as Sentry from "@sentry/react";
import { Link } from 'react-router-dom';
import { LoadingState } from './Common/PanelState';

// --- Chart Components (Styled for new theme) ---
function SixMonthComparisonChart({ chartData }) {
    const data = {
        labels: chartData.labels,
        datasets: [
            {
                label: 'Business', data: chartData.totalBusiness,
                borderColor: '#c4151c', backgroundColor: 'rgba(196, 21, 28, 0.1)',
                type: 'line', tension: 0.4, yAxisID: 'y1', borderWidth: 3, fill: true,
            },
            { label: 'Received', data: chartData.totalReceived, backgroundColor: 'rgba(33, 37, 41, 0.8)', yAxisID: 'y', barPercentage: 0.6 },
            { label: 'Expenses', data: chartData.totalExpenses, backgroundColor: 'rgba(206, 212, 218, 0.8)', yAxisID: 'y', barPercentage: 0.6 },
        ],
    };
    const options = { responsive: true, interaction: { mode: 'index', intersect: false }, plugins: { legend: { position: 'top' }, title: { display: true, text: '6-Month Financial Overview' } }, scales: { y: { beginAtZero: true, stacked: true }, x: { stacked: true }, y1: { type: 'linear', display: true, position: 'right', grid: { drawOnChartArea: false }, beginAtZero: true } } };
    return <Bar options={options} data={data} />;
}

function ExpenseBreakdownChart({ data }) {
    const chartData = {
        labels: data.map(d => d.category),
        datasets: [{
            data: data.map(d => d.amount),
            backgroundColor: ['#c4151c', '#212529', '#6c757d', '#adb5bd', '#dee2e6', '#f8f9fa'],
            borderColor: '#FFFFFF', borderWidth: 2,
        }]
    };
    const options = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'right' }, title: { display: true, text: 'Expense Breakdown', font: { size: 16 } } } };
    return <Pie data={chartData} options={options} />;
}

// --- Reusable Stat Card Component ---
const StatCard = ({ title, value, icon }) => (
    <div className="bg-white p-6 rounded-xl shadow-md flex items-center gap-5 transition-transform hover:scale-105">
        <div className="p-4 bg-red-50 rounded-full">
            <span className="text-3xl text-[#c4151c]">{icon}</span>
        </div>
        <div>
            <p className="text-3xl font-bold text-gray-800">{value}</p>
            <p className="text-sm font-medium text-gray-500">{title}</p>
        </div>
    </div>
);

function DashboardSection() {
    const { db } = useFirebase();
    const [financials, setFinancials] = useState({ totalBusiness: 0, totalReceived: 0, pending: 0, totalExpenses: 0, net: 0 });
    const [summaryCounts, setSummaryCounts] = useState({ clients: 0, activeBookings: 0, totalInvoices: 0, employees: 0 });
    const [expenseChartData, setExpenseChartData] = useState(null);
    const [chartData, setChartData] = useState(null);
    const [filterYear, setFilterYear] = useState(new Date().getFullYear());
    const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!db) return;
        setIsLoading(true);

        const fetchData = async () => {
            try {
                const startOfMonth = Timestamp.fromDate(new Date(filterYear, filterMonth - 1, 1));
                const endOfMonth = Timestamp.fromDate(new Date(filterYear, filterMonth, 0, 23, 59, 59));
                
                const [clientsSnap, invoicesSnap, workersSnap] = await Promise.all([
                    getDocs(query(collection(db, 'clients'), limit(500))),
                    getDocs(query(collection(db, 'invoices'), limit(500))),
                    getDocs(query(collection(db, 'workers'), limit(100))),
                ]);
                // Simplified active bookings logic for now
                setSummaryCounts({ clients: clientsSnap.size, activeBookings: 0, totalInvoices: invoicesSnap.size, employees: workersSnap.size });

                const monthlySalesQuery = query(collection(db, 'invoices'), where('invoiceDate', '>=', startOfMonth), where('invoiceDate', '<=', endOfMonth));
                const monthlyExpensesQuery = query(collection(db, 'expenses'), where('date', '>=', startOfMonth), where('date', '<=', endOfMonth));
                const [monthlySalesSnap, monthlyExpensesSnap] = await Promise.all([getDocs(monthlySalesQuery), getDocs(monthlyExpensesSnap)]);
                
                const totalBusiness = monthlySalesSnap.docs.reduce((sum, doc) => sum + (doc.data().totalAmount || 0), 0);
                const totalReceived = monthlySalesSnap.docs.reduce((sum, doc) => sum + (doc.data().totalPaid || 0), 0);
                const totalPending = monthlySalesSnap.docs.reduce((sum, doc) => sum + (doc.data().pendingAmount || 0), 0);
                const totalExpenses = monthlyExpensesSnap.docs.reduce((sum, doc) => sum + (doc.data().amount || 0), 0);
                setFinancials({ totalBusiness, totalReceived, pending: totalPending, totalExpenses, net: totalBusiness - totalExpenses });

                const expenseByCategory = monthlyExpensesSnap.docs.reduce((acc, doc) => {
                    const cat = doc.data().category || 'Other'; acc[cat] = (acc[cat] || 0) + (doc.data().amount || 0); return acc;
                }, {});
                setExpenseChartData(Object.entries(expenseByCategory).map(([category, amount]) => ({ category, amount })));

                // Placeholder for 6-month chart data
                setChartData({ labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'], totalBusiness: [10,20,15,25,30,22], totalReceived: [8,18,12,22,25,20], totalExpenses: [5,6,5,7,8,6] });

            } catch (err) {
                 toast.error("Failed to load dashboard data.");
                 if (window.Sentry) Sentry.captureException(err);
            } finally { setIsLoading(false); }
        };
        fetchData();
    }, [db, filterYear, filterMonth]);

    if (isLoading) {
        return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                <LoadingState label="Loading dashboard" />
            </div>
        );
    }

    return (
        <div className="container mx-auto">
            <div className="mb-8 flex flex-col sm:flex-row justify-between sm:items-center gap-4">
                <div>
                    <h1 className="admin-page-title">Dashboard</h1>
                    <p className="admin-page-sub">A quick overview of your business performance.</p>
                </div>
                <div className="flex gap-2">
                    <Link to="/sales" className="bg-[#c4151c] text-white font-bold py-3 px-5 rounded-lg shadow-md hover:bg-red-700 transition-all">+ New Sale</Link>
                    <Link to="/expenses" className="bg-gray-700 text-white font-bold py-3 px-5 rounded-lg shadow-md hover:bg-gray-800 transition-all">+ New Expense</Link>
                </div>
            </div>

            <div className="bg-white p-4 rounded-xl shadow-md mb-8">
                <div className="flex items-center gap-4">
                    <label className="text-sm font-medium text-gray-600">Month:</label>
                    <select value={filterMonth} onChange={e => setFilterMonth(Number(e.target.value))} className="p-2 bg-gray-50 rounded-lg border">
                        {Array.from({ length: 12 }, (_, i) => <option key={i+1} value={i+1}>{new Date(0, i).toLocaleString('en-US', { month: 'long' })}</option>)}
                    </select>
                    <label className="text-sm font-medium text-gray-600">Year:</label>
                    <select value={filterYear} onChange={e => setFilterYear(Number(e.target.value))} className="p-2 bg-gray-50 rounded-lg border">
                        {Array.from({ length: 5 }, (_, i) => <option key={i} value={new Date().getFullYear() - i}>{new Date().getFullYear() - i}</option>)}
                    </select>
                </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard title="Total Clients" value={summaryCounts.clients} icon="🏢" />
                <StatCard title="Total Invoices" value={summaryCounts.totalInvoices} icon="🧾" />
                <StatCard title="Active Bookings" value={summaryCounts.activeBookings} icon="✅" />
                <StatCard title="Team Members" value={summaryCounts.employees} icon="👥" />
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 mt-8">
                <h2 className="text-xl font-bold text-gray-800 mb-4">Financial Summary <span className="text-base font-normal text-gray-500">(for selected month)</span></h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 text-center">
                    <div className="p-4 bg-blue-50 rounded-lg"><p className="text-sm font-medium text-blue-700">Total Business</p><p className="text-2xl font-bold mt-1">PKR {financials.totalBusiness.toLocaleString()}</p></div>
                    <div className="p-4 bg-green-50 rounded-lg"><p className="text-sm font-medium text-green-700">Received</p><p className="text-2xl font-bold mt-1">PKR {financials.totalReceived.toLocaleString()}</p></div>
                    <div className="p-4 bg-orange-50 rounded-lg"><p className="text-sm font-medium text-orange-700">Pending</p><p className="text-2xl font-bold mt-1">PKR {financials.pending.toLocaleString()}</p></div>
                    <div className="p-4 bg-gray-100 rounded-lg"><p className="text-sm font-medium text-gray-700">Expenses</p><p className="text-2xl font-bold mt-1">PKR {financials.totalExpenses.toLocaleString()}</p></div>
                    <div className={`p-4 rounded-lg ${financials.net >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}><p className={`text-sm font-medium ${financials.net >= 0 ? 'text-emerald-800' : 'text-red-800'}`}>Net Profit</p><p className="text-2xl font-bold mt-1">PKR {financials.net.toLocaleString()}</p></div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mt-8">
                <div className="lg:col-span-3 bg-white rounded-xl shadow-md p-6">
                    {chartData ? <SixMonthComparisonChart chartData={chartData} /> : <p>Loading chart...</p>}
                </div>
                <div className="lg:col-span-2 bg-white rounded-xl shadow-md p-6">
                    {expenseChartData && expenseChartData.length > 0 ? <ExpenseBreakdownChart data={expenseChartData} /> : <div className="text-center text-gray-500 h-full flex items-center justify-center"><p>No expense data for this month.</p></div>}
                </div>
            </div>
         </div>
    );
}

export default DashboardSection;